import React, { useMemo } from "react";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

const DEFAULT_THRESHOLD = 10;
const OTHER_LABEL = "Other";

/**
 * Collapse items beyond threshold into a single "Other" bucket.
 * Items should be pre-sorted by value descending.
 *
 * @param {Array<{ id: string, label: string, value: number }>} items sorted by value desc
 * @param {number} threshold max number of distinct columns (beyond this, collapse into Other)
 * @param {string} [collapseLabel="10+ games"] label for the collapsed column
 * @returns {{ labels: string[], barData: number[], otherLabel: string }}
 */
export const collapseByThreshold = (items, threshold = DEFAULT_THRESHOLD, collapseLabel = "10+ games") => {
  if (items.length === 0) {
    return { labels: [], barData: [], otherLabel: OTHER_LABEL };
  }

  const keepCount = Math.min(threshold - 1, items.length);
  const keep = items.slice(0, keepCount);
  const rest = items.slice(keepCount);

  const labels = keep.map((item) => item.label);
  const barData = keep.map((item) => item.value);

  if (rest.length > 0) {
    const otherValue = rest.reduce((sum, item) => sum + item.value, 0);
    labels.push(collapseLabel);
    barData.push(otherValue);
  }

  return { labels, barData, otherLabel: OTHER_LABEL };
};

/**
 * Compute cumulative percentage from bar values (same order).
 *
 * @param {number[]} barData
 * @returns {number[]}
 */
const cumulativePercent = (barData) => {
  const total = barData.reduce((s, v) => s + v, 0);
  if (total === 0) return barData.map(() => 0);
  let cum = 0;
  return barData.map((v) => {
    cum += v;
    return Math.round((cum / total) * 100);
  });
};

/**
 * Generic Pareto chart: bar series (value per category) + line series (cumulative %).
 * When there are threshold or more categories, the smallest are collapsed into a single "Other" column.
 *
 * @param {object} props
 * @param {Array<{ id: string, label: string, value: number }>} props.items categories with values (any order; sorted internally by value desc)
 * @param {number} [props.threshold=10] max columns before collapsing remainder into one (e.g. 10 → show top 9 + collapseLabel)
 * @param {string} [props.collapseLabel="10+ games"] label for the collapsed column when over threshold
 * @param {(value: number) => string} [props.valueFormatter] format bar values (e.g. currency)
 * @param {number} [props.height=280]
 * @param {boolean} [props.isLoading]
 */
export const ParetoChart = ({
  items,
  threshold = DEFAULT_THRESHOLD,
  collapseLabel = "10+ games",
  valueFormatter = (v) => String(v),
  height = 280,
  isLoading = false,
}) => {
  const { labels, barData, cumulativePercentData } = useMemo(() => {
    const sorted = [...items].sort((a, b) => b.value - a.value);
    const { labels: L, barData: B } = collapseByThreshold(sorted, threshold, collapseLabel);
    const cumulativePercentData = cumulativePercent(B);
    return { labels: L, barData: B, cumulativePercentData };
  }, [items, threshold, collapseLabel]);

  if (isLoading) {
    return (
      <Box height={height} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (labels.length === 0) {
    return null;
  }

  return (
    <ChartContainer
      height={height}
      series={[
        {
          type: "bar",
          data: barData,
          yAxisId: "value",
          label: "Value",
          valueFormatter: (v) => valueFormatter(v),
        },
        {
          type: "line",
          data: cumulativePercentData,
          yAxisId: "percent",
          label: "Cumulative %",
          valueFormatter: (v) => `${v}%`,
        },
      ]}
      xAxis={[
        {
          scaleType: "band",
          data: labels,
        },
      ]}
      yAxis={[
        {
          id: "value",
          position: "left",
          valueFormatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)),
        },
        {
          id: "percent",
          position: "right",
          label: "Cumulative %",
          valueFormatter: (v) => `${v}%`,
          min: 0,
          max: 100,
        },
      ]}
    >
      <BarPlot />
      <LinePlot />
      <ChartsXAxis />
      <ChartsYAxis axisId="value" />
      <ChartsYAxis axisId="percent" />
      <ChartsTooltip />
    </ChartContainer>
  );
};
